import { Question, QuestionType, NPS, Rating, SingleSelect, MultipleSelect, Text, Image, DropDown } from '@/types/QuestionTypes';

// Core type definitions
export type LOGIC_OPERATORS =
  // Existing operators
  | "EQUALS" | "equals"
  | "NOT_EQUALS" | "notEquals"
  | "CONTAINS" | "contains"
  | "DOES_NOT_CONTAIN" | "doesNotContain"
  | "DOES_NOT_CONTAINS" | "doesNotContains"
  | "IS_ANSWERED" | "isAnswered"
  | "IS_UNANSWERED" | "isUnanswered"
  | "LESS_THAN" | "lessThan"
  | "GREATER_THAN" | "greaterThan"
  | "LESS_THAN_OR_EQUAL_TO" | "lessThanOrEqualTo"
  | "GREATER_THAN_OR_EQUAL_TO" | "greaterThanOrEqualTo"
  | "BETWEEN" | "between"
  | "OUTSIDE" | "outside"
  | "IN" | "in"
  | "NOT_IN" | "notIn"
  // New simplified operators
  | "IS" | "is"
  | "IS_NOT" | "isNot"
  | "IS_EQUAL_TO" | "isEqualTo"
  | "IS_NOT_EQUAL_TO" | "isNotEqualTo"
  | "GREATER_THAN_OR_EQUAL" | "greaterThanOrEqual"
  | "LESS_THAN_OR_EQUAL" | "lessThanOrEqual";  

export type ACTION_TYPES =
  | "REDIRECT" | "redirect"
  | "JUMP_TO" | "jumpTo"
  | "SHOW" | "show"
  | "HIDE" | "hide"
  | "END_SURVEY" | "endSurvey";

export type AnswerValue = string | number | boolean | string[] | number[] | null | undefined;
export type AnswerRecord = Record<string, AnswerValue>;

// Core state interface
export interface RuleEngineState {
  questions: Record<string, Question>;
  questionSequence: string[];
  answers: AnswerRecord;
  currentPath: string[];
}

// Advanced rule types
export interface AdvancedCondition {
  questionId: string;
  operator: LOGIC_OPERATORS;
  value?: any;
  values?: any[];
}

export interface AdvancedAction {
  type: ACTION_TYPES;
  questionIds?: string[];
}

// Support mixed AND/OR expressions
export interface ConditionExpression {
  type: 'condition';
  condition: AdvancedCondition;
}

export interface LogicExpression {
  type: 'logic';
  operator: 'AND' | 'OR';
  left: ConditionExpression | LogicExpression;
  right: ConditionExpression | LogicExpression;
}

export type MixedExpression = ConditionExpression | LogicExpression;

export interface AdvancedRule {
  ruleId: string;
  surveyId: string;
  ruleName?: string;
  priority: number;
  isActive: boolean;
  sourceQuestionId?: string;
  ifConditions: AdvancedCondition[];
  conditionLogic: 'AND' | 'OR';
  ifExpression?: MixedExpression; 
  thenActions: AdvancedAction[];
  otherwiseAction?: AdvancedAction; // Single fallback action when conditions are not met
}

// Type guards
const isString = (value: unknown): value is string => typeof value === 'string';
const isNumber = (value: unknown): value is number => typeof value === 'number' && !isNaN(value);
const isArray = (value: unknown): value is unknown[] => Array.isArray(value);
const isObject = (value: unknown): value is Record<string, unknown> => 
  value !== null && typeof value === 'object' && !Array.isArray(value);

// Safe property access utility
const safeGet = <T>(obj: unknown, path: string, defaultValue: T): T => {
  try {
    if (!isObject(obj)) return defaultValue;
    const keys = path.split('.');
    let current: any = obj;
    
    for (const key of keys) {
      if (current === null || current === undefined) return defaultValue;
      current = current[key];
    }
    
    return current !== undefined ? current : defaultValue;
  } catch {
    return defaultValue;
  }
};

// Operator normalization
function normalizeOperator(operator: unknown): LOGIC_OPERATORS {
  if (!isString(operator)) return 'IS';

  const normalizedOp = operator.toUpperCase().trim();
  const operatorMap: Record<string, LOGIC_OPERATORS> = {
    // Legacy mappings
    'CONTAINS': 'IS',
    'DOES_NOT_CONTAINS': 'IS_NOT',
    'DOESNOTCONTAINS': 'IS_NOT',
    'DOES_NOT_CONTAIN': 'IS_NOT',
    'DOESNOTCONTAIN': 'IS_NOT',
    // Standard mappings
    'ISANSWERED': 'IS_ANSWERED',
    'ISUNANSWERED': 'IS_UNANSWERED',
    'ISNOT': 'IS_NOT',
    'NOTEQUALS': 'NOT_EQUALS',
    'GREATERTHAN': 'GREATER_THAN',
    'GREATERTHANOREQUAL': 'GREATER_THAN_OR_EQUAL',
    'GREATERTHANOREQUALTO': 'GREATER_THAN_OR_EQUAL_TO',
    'LESSTHAN': 'LESS_THAN',
    'LESSTHANOREQUAL': 'LESS_THAN_OR_EQUAL',
    'LESSTHANOREQUALTO': 'LESS_THAN_OR_EQUAL_TO',
    'ISEQUALTO': 'IS_EQUAL_TO',
    'ISNOTEQUALTO': 'IS_NOT_EQUAL_TO'
  };

  return operatorMap[normalizedOp] || (normalizedOp as LOGIC_OPERATORS) || 'IS';
}

// Option normalization
function normalizeOptions(options: unknown): Array<{ id: string; text: string }> {
  if (!isArray(options)) return [];
  
  return options.map((opt, index) => {
    if (isString(opt)) {
      return { id: `opt-${index}`, text: opt };
    }
    
    if (isObject(opt)) {
      const id = safeGet(opt, 'id', `opt-${index}`);
      const text = safeGet(opt, 'text', String(opt.value || opt.label || `Option ${index + 1}`));
      return { id: String(id), text: String(text) };
    }
    
    return { id: `opt-${index}`, text: String(opt) };
  });
}

function normalizeOptionsImage(options: unknown): Array<{ id: string; text: string; imageUrl: string; metadata?: Record<string, any>; responsive?: boolean }> {
  if (!isArray(options)) return [];
  
  return options.map((opt, index) => {
    if (isString(opt)) {
      return { id: `opt-${index}`, text: opt, imageUrl: opt };
    }
    
    if (isObject(opt)) {
      const id = safeGet(opt, 'id', `opt-${index}`);
      const text = safeGet(opt, 'text', String(opt.value || opt.label || `Option ${index + 1}`));
      const imageUrl = safeGet(opt, 'imageUrl', String(opt.imageUrl || `Option ${index + 1}`));
      const metadata = safeGet(opt, 'metadata', {});
      const responsive = Boolean(safeGet(opt, 'responsive', false));
      return { 
        id: String(id), 
        text: String(text), 
        imageUrl: String(imageUrl),
        metadata: isObject(metadata) ? metadata : {},
        responsive
      };
    }
    
    return { id: `opt-${index}`, text: String(opt), imageUrl: String(opt) };
  });
}

// Config normalization functions
function normalizeSingleSelectConfig(config: unknown): SingleSelect['config'] {
  if (!isObject(config)) config = {};
  
  return {
    options: normalizeOptions(safeGet(config, 'options', [])),
    randomize: Boolean(safeGet(config, 'randomize', false)),
    allowOther: Boolean(safeGet(config, 'allowOther', false)),
    otherLabel: String(safeGet(config, 'otherLabel', 'Other')),
    displayType: safeGet(config, 'displayType', 'radio') as 'radio' | 'pill'
  };
}

function normalizeMultiSelectConfig(config: unknown): MultipleSelect['config'] {
  if (!isObject(config)) config = {};
  
  const maxSelect = safeGet(config, 'maxSelect', null);
  const minSelect = safeGet(config, 'minSelect', null);
  
  return {
    options: normalizeOptions(safeGet(config, 'options', [])),
    maxSelect: isNumber(maxSelect) ? maxSelect : null,
    minSelect: isNumber(minSelect) ? minSelect : null,
    randomize: Boolean(safeGet(config, 'randomize', false)),
    allowOther: Boolean(safeGet(config, 'allowOther', false)),
    otherLabel: String(safeGet(config, 'otherLabel', 'Other')),
    displayType: safeGet(config, 'displayType', 'checkbox') as 'checkbox'
  };
}

function normalizeRatingConfig(config: unknown): Rating['config'] {
  if (!isObject(config)) config = {};
  
  const scale = safeGet(config, 'scale', 5);
  
  return {
    scale: (isNumber(scale) && (scale === 5 || scale === 10)) ? scale : 5,
    maxLabel: String(safeGet(config, 'maxLabel', 'Excellent')),
    minLabel: String(safeGet(config, 'minLabel', 'Poor')),
    displayType: safeGet(config, 'displayType', 'star') as 'star' | 'number' | 'emoji'
  };
}

function normalizeNPSConfig(config: unknown): NPS['config'] {
  if (!isObject(config)) config = {};
  
  return {
    maxLabel: String(safeGet(config, 'maxLabel', 'Very Likely')),
    minLabel: String(safeGet(config, 'minLabel', 'Not at all Likely')),
    displayType: safeGet(config, 'displayType', 'number') as 'number' | 'emoji'
  };
}

function normalizeTextConfig(config: unknown): Text['config'] {
  if (!isObject(config)) config = {};
  
  return {
    placeholder: String(safeGet(config, 'placeholder', ''))
  };
}

function normalizeImageConfig(config: unknown): Image['config'] {
  if (!isObject(config)) config = {};
  
  return {
    options: normalizeOptionsImage(safeGet(config, 'options', [])),
    randomize: Boolean(safeGet(config, 'randomize', false)),
    hideLabel: Boolean(safeGet(config, 'hideLabel', false)),
  };
}

function normalizeDropDownConfig(config: unknown): DropDown['config'] {
  if (!isObject(config)) config = {};
  
  return {
    options: normalizeOptions(safeGet(config, 'options', [])),
    randomize: Boolean(safeGet(config, 'randomize', false)),
    allowOther: Boolean(safeGet(config, 'allowOther', false)),
    otherLabel: String(safeGet(config, 'otherLabel', 'Other')),
  };
}

// Question normalization
function normalizeQuestion(q: unknown, index: number): Question | null {
  try {
    if (!isObject(q)) return null;

    const questionId = safeGet(q, 'question_id', '') || safeGet(q, 'questionId', '') || safeGet(q, 'id', '');
    if (!questionId) return null;

    const surveyId = safeGet(q, 'survey_id', '') || safeGet(q, 'surveyId', '');
    const bankId = safeGet(q, 'bank_id', '') || safeGet(q, 'bankId', '');
    const text = String(safeGet(q, 'text', ''));
    const description = String(safeGet(q, 'description', ''));
    const type = safeGet(q, 'type', 'text') as QuestionType;
    const isRequired = Boolean(safeGet(q, 'is_required', false) || safeGet(q, 'isRequired', false));
    const orderIndex = Number(safeGet(q, 'order_index', 0) || safeGet(q, 'orderIndex', 0)) || 0;

    const createdAt = safeGet(q, 'created_at', null) || safeGet(q, 'createdAt', null);
    const updatedAt = safeGet(q, 'updated_at', null) || safeGet(q, 'updatedAt', null);
    const deletedAt = safeGet(q, 'deleted_at', null) || safeGet(q, 'deletedAt', null);

    let config = safeGet(q, 'config', {});
    let validation = safeGet(q, 'validation', {});
    let logic = safeGet(q, 'logic', {});

    if (isObject(logic)) {
      if (!isArray(logic.rules)) {
        logic.rules = [];
      }
      
      if (isObject(config)) {
        if (safeGet(config, 'isTerminal', false) && !logic.isTerminal) {
          logic.isTerminal = true;
        }
        if (safeGet(config, 'nextQuestionId', '') && !logic.defaultNextQuestionId) {
          logic.defaultNextQuestionId = safeGet(config, 'nextQuestionId', '');
        }
      }
    }

    const baseQuestion = {
      questionId: String(questionId),
      surveyId: String(surveyId),
      bankId: String(bankId),
      text,
      description,
      type,
      isRequired,
      orderIndex,
      validation: isObject(validation) ? validation : {},
      logic: isObject(logic) ? logic : {},
      createdAt,
      updatedAt,
      deletedAt,
      SurveySurveyId: safeGet(q, 'SurveySurveyId', null),
      bankQuestionBankId: safeGet(q, 'bankQuestionBankId', null),
      QuestionBankBankId: safeGet(q, 'QuestionBankBankId', null)
    };

    switch (type) {
      case 'single_select':
        return { ...baseQuestion, config: normalizeSingleSelectConfig(config) } as SingleSelect;
      case 'multi_select':
        return { ...baseQuestion, config: normalizeMultiSelectConfig(config) } as MultipleSelect;
      case 'rating':
        return { ...baseQuestion, config: normalizeRatingConfig(config) } as Rating;
      case 'nps':
        return { ...baseQuestion, config: normalizeNPSConfig(config) } as NPS;
      case 'text':
        return { ...baseQuestion, config: normalizeTextConfig(config) } as Text;
      case 'image':
        return { ...baseQuestion, config: normalizeImageConfig(config) } as Image;
      case 'drop_down':
        return { ...baseQuestion, config: normalizeDropDownConfig(config) } as DropDown_;
      default:
        return { ...baseQuestion, config: isObject(config) ? config : {} } as Question;
    }
  } catch {
    return null;
  }
}

// Question data normalization
export function normalizeQuestionData(questions: unknown[]): Record<string, Question> {
  if (!isArray(questions)) return {};

  const normalizedQuestions: Record<string, Question> = {};

  questions.forEach((q, index) => {
    const normalizedQuestion = normalizeQuestion(q, index);
    if (normalizedQuestion) {
      normalizedQuestions[normalizedQuestion.questionId] = normalizedQuestion;
    }
  });

  return normalizedQuestions;
}

// State creation
export function createRuleEngineState(
  questions: Record<string, Question> | unknown[],
  questionSequence: string[],
  answers: AnswerRecord = {}
): RuleEngineState {
  const normalizedQuestions = isArray(questions) 
    ? normalizeQuestionData(questions)
    : (isObject(questions) ? questions as Record<string, Question> : {});

  const validSequence = isArray(questionSequence) ? questionSequence.filter(isString) : [];
  const safeAnswers = isObject(answers) ? answers as AnswerRecord : {};

  return {
    questions: normalizedQuestions,
    questionSequence: validSequence,
    answers: safeAnswers,
    currentPath: validSequence.length > 0 ? [validSequence[0]] : []
  };
}

// Answer updates
export function updateAnswers(state: RuleEngineState, newAnswers: AnswerRecord): RuleEngineState {
  if (!isObject(newAnswers)) return state;

  return {
    ...state,
    answers: { ...state.answers, ...newAnswers }
  };
}

export function setAnswer(state: RuleEngineState, questionId: string, value: AnswerValue): RuleEngineState {
  if (!isString(questionId) || !questionId.trim()) return state;

  let newPath = [...state.currentPath];

  if (!newPath.includes(questionId)) {
    newPath.push(questionId);
  }

  const questionIndex = newPath.indexOf(questionId);
  const isCurrentQuestion = questionIndex === newPath.length - 1;
  const isMiddleQuestion = questionIndex >= 0 && questionIndex < newPath.length - 1;

  if (isCurrentQuestion) {
    const nextQuestionResult = getAdvancedNextQuestion(state, questionId, []);
    if (nextQuestionResult.questionId && !newPath.includes(nextQuestionResult.questionId)) {
      newPath.push(nextQuestionResult.questionId);
    }
  } else if (isMiddleQuestion) {
    newPath = newPath.slice(0, questionIndex + 1);
    const nextQuestionResult = getAdvancedNextQuestion(state, questionId, []);
    if (nextQuestionResult.questionId) {
      newPath.push(nextQuestionResult.questionId);
    }
  }

  return {
    ...state,
    answers: { ...state.answers, [questionId]: value },
    currentPath: newPath
  };
}

// Condition evaluation
export function evaluateCondition(
  state: RuleEngineState,
  questionId: string,
  condition: unknown,
  answer: AnswerValue,
  currentQuestionId?: string
): boolean {
  try {

    if (!isObject(condition)) return false;


    const question = state.questions[questionId];


    if (!question) {
      // Fallback: try to evaluate without question type if we have basic condition info
      const fallbackOperator = safeGet(condition, 'operator', '') || safeGet(condition, 'OPERATOR', '');
      const fallbackConditionValue = safeGet(condition, 'value', undefined) !== undefined 
        ? safeGet(condition, 'value', null)
        : safeGet(condition, 'values', undefined) !== undefined
          ? safeGet(condition, 'values', null)
          : safeGet(condition, 'VALUE', null);
      
      if (fallbackOperator && fallbackConditionValue !== undefined) {
        return evaluateGenericCondition(fallbackOperator, answer, fallbackConditionValue, state);
      }
      return false;
    }

    const operator = safeGet(condition, 'operator', '') || safeGet(condition, 'OPERATOR', '');
    let conditionValue: any = safeGet(condition, 'value', undefined) !== undefined 
      ? safeGet(condition, 'value', null)
      : safeGet(condition, 'values', undefined) !== undefined
        ? safeGet(condition, 'values', null)
        : safeGet(condition, 'VALUE', null);
    


    // Fix double array nesting - if value is already an array, use it directly
    if (Array.isArray(conditionValue) && conditionValue.length > 0 && typeof conditionValue[0] === 'string') {
      // Already properly formatted array
    } else if (conditionValue && typeof conditionValue === 'string') {
      conditionValue = [conditionValue];
    }
    
    // Additional check for nested arrays
    if (Array.isArray(conditionValue) && conditionValue.length > 0 && Array.isArray(conditionValue[0])) {
      conditionValue = conditionValue[0];
    }

    if (!operator) return false;

    const normalizedOperator = normalizeOperator(operator);

    // if (normalizedOperator === 'IS_ANSWERED' || normalizedOperator === 'isAnswered') {
    //   const isAnswered = answer !== undefined && answer !== null && answer !== '';
    //   console.log(`[JPT-DEBUG] IS_ANSWERED check: answer=${JSON.stringify(answer)}, result=${isAnswered}`);
    //   return isAnswered;
    // }

    // if (normalizedOperator === 'IS_UNANSWERED' || normalizedOperator === 'isUnanswered') {
    //   const isUnanswered = answer === undefined || answer === null || answer === '';
    //   console.log(`[JPT-DEBUG] IS_UNANSWERED check: answer=${JSON.stringify(answer)}, result=${isUnanswered}`);
    //   return isUnanswered;
    // }

    // if (answer === undefined || answer === null) {
    //   if (normalizedOperator === 'DOES_NOT_CONTAINS' && (conditionValue === null || conditionValue === undefined)) {
    //     return true;
    //   }
    //   return false;
    // }



    
    switch (question.type) {
      case 'single_select':
      case 'image':
        return evaluateSingleSelectCondition(normalizedOperator, answer, conditionValue, state, questionId, currentQuestionId, question);

      case 'multi_select':
        return evaluateMultiSelectCondition(normalizedOperator, answer, conditionValue, state, questionId, currentQuestionId, question);

      case 'rating':
        return evaluateRatingCondition(normalizedOperator, answer, conditionValue, state, questionId, currentQuestionId);

      case 'nps':
        return evaluateNPSCondition(normalizedOperator, answer, conditionValue, state, questionId, currentQuestionId);

      case 'text':
        return evaluateTextCondition(normalizedOperator, answer, conditionValue, state, questionId, currentQuestionId);

      default:
        return evaluateGenericCondition(normalizedOperator, answer, conditionValue, state, questionId, currentQuestionId);
    }
  } catch {
    return false;
  }
}

// Condition evaluation functions
function evaluateSingleSelectCondition(
  operator: LOGIC_OPERATORS,
  answer: AnswerValue,
  conditionValue: unknown,
  state: RuleEngineState,
  questionId?: string,
  currentQuestionId?: string,
  question?: Question
): boolean {
  try {

    if (operator === 'IS_ANSWERED' || operator === 'isAnswered') {
      // Only check if answered for questions that come before or at current position
      if (questionId && currentQuestionId) {
        const questionIndex = state.questionSequence.indexOf(questionId);
        const currentIndex = state.questionSequence.indexOf(currentQuestionId);
        
        if (questionIndex > currentIndex) {
          return false; 
        }
      }
      
      const isAnswered = answer !== undefined && answer !== null && answer !== '';
      return isAnswered;
    }

    if (operator === 'IS_UNANSWERED' || operator === 'isUnanswered') {
      // Only check if unanswered for questions that come before or at current position
      if (questionId && currentQuestionId) {
        const questionIndex = state.questionSequence.indexOf(questionId);
        const currentIndex = state.questionSequence.indexOf(currentQuestionId);
        
        if (questionIndex >= currentIndex) {
          return false; // Current and future questions can't be "unanswered" yet
        }
      }
      
      const isUnanswered = answer === undefined || answer === null || answer === '';
      return isUnanswered;
    }

    const answerStr = String(answer);
    
    // PROPER ARRAY FLATTENING - handle nested arrays correctly
    let flattenedValue = conditionValue;
    
    // Flatten nested arrays like [["5"]] to ["5"]
    if (Array.isArray(conditionValue) && conditionValue.length > 0 && Array.isArray(conditionValue[0])) {
      flattenedValue = conditionValue[0];
    }
    
    // Helper function to compare answer with condition values (handles text-to-UUID conversion)
    const compareValues = (answer: string, conditionVals: string[]): boolean => {
      // Direct text match
      if (conditionVals.includes(answer)) {
        return true;
      }
      
      // If we have question options, try UUID-to-text and text-to-UUID conversion
      if (question && question.config && 'options' in question.config && question.config.options) {
        const options = question.config.options;
        
        // Convert condition values to both text and UUID for comparison
        const expandedConditionVals = new Set<string>();
        conditionVals.forEach(val => {
          expandedConditionVals.add(val); // Original value
          
          // Find option by text -> add UUID
          const optionByText = options.find(opt => opt.text.toLowerCase() === val.toLowerCase());
          if (optionByText) {
            expandedConditionVals.add(optionByText.id);
          }
          
          // Find option by UUID -> add text
          const optionById = options.find(opt => opt.id === val);
          if (optionById) {
            expandedConditionVals.add(optionById.text);
          }
        });
        
        // Check if answer matches any expanded values
        if (expandedConditionVals.has(answer)) {
          return true;
        }
        
        // Also check if answer (as UUID) maps to text that matches condition
        const answerOption = options.find(opt => opt.id === answer);
        if (answerOption && expandedConditionVals.has(answerOption.text)) {
          return true;
        }
        
        // Check if answer (as text) maps to UUID that matches condition
        const textToUuidOption = options.find(opt => opt.text.toLowerCase() === answer.toLowerCase());
        if (textToUuidOption && expandedConditionVals.has(textToUuidOption.id)) {
          return true;
        }
      }
      
      return false;
    };
    
    switch (operator) {
      case 'IS':
      case 'is':
      case 'CONTAINS':  
        if (isArray(flattenedValue)) {
          return compareValues(answerStr, flattenedValue.map(String));
        }
        return compareValues(answerStr, [String(flattenedValue)]);

      case 'IS_NOT':
      case 'isNot':
      case 'DOES_NOT_CONTAINS':  
      case 'doesNotContains':
      case 'DOES_NOT_CONTAIN':
      case 'doesNotContain':   
        if (isArray(flattenedValue)) {
          return !compareValues(answerStr, flattenedValue.map(String));
        }
        return !compareValues(answerStr, [String(flattenedValue)]);

      default:
        return false;
    }
  } catch (error) {
    console.error(`[RE] ❌ Single-select evaluation failed for question:`, error);
    return false;
  }
}

function evaluateMultiSelectCondition(
  operator: LOGIC_OPERATORS,
  answer: AnswerValue,
  conditionValue: unknown,
  state: RuleEngineState,
  questionId?: string,
  currentQuestionId?: string,
  question?: Question
): boolean {
  try {
    if (operator === 'IS_ANSWERED' || operator === 'isAnswered') {
      // Only check if answered for questions that come before or at current position
      if (questionId && currentQuestionId) {
        const questionIndex = state.questionSequence.indexOf(questionId);
        const currentIndex = state.questionSequence.indexOf(currentQuestionId);
        
        if (questionIndex >= currentIndex) {
          return false; // Current and future questions can't be "answered" yet
        }
      }
      
      const isAnswered = answer !== undefined && answer !== null && 
        isArray(answer) && answer.length > 0;
      return isAnswered;
    }

    if (operator === 'IS_UNANSWERED' || operator === 'isUnanswered') {
      // Only check if unanswered for questions that come before or at current position
      if (questionId && currentQuestionId) {
        const questionIndex = state.questionSequence.indexOf(questionId);
        const currentIndex = state.questionSequence.indexOf(currentQuestionId);
        
        if (questionIndex >= currentIndex) {
          return false; // Current and future questions can't be "unanswered" yet
        }
      }
      
      const isUnanswered = answer === undefined || answer === null || 
        !isArray(answer) || answer.length === 0;
      return isUnanswered;
    }

    if (!isArray(answer)) return false;

    const answerArray = answer.map(String);

    // Helper function to compare answer array with condition values (handles text-to-UUID conversion)
    const compareMultiSelectValues = (answers: string[], conditionVals: string[]): boolean => {
      // Direct matches
      for (const ans of answers) {
        if (conditionVals.includes(ans)) {
          return true;
        }
      }
      
      // If we have question options, try UUID-to-text and text-to-UUID conversion
      if (question && question.config && 'options' in question.config && question.config.options) {
        const options = question.config.options;
        
        // Create expanded sets for both answers and condition values
        const expandedAnswers = new Set<string>();
        const expandedConditions = new Set<string>();
        
        // Expand answers (add both UUID and text versions)
        answers.forEach(ans => {
          expandedAnswers.add(ans); // Original answer
          
          // Find option by UUID -> add text
          const optionById = options.find(opt => opt.id === ans);
          if (optionById) {
            expandedAnswers.add(optionById.text);
          }
          
          // Find option by text -> add UUID
          const optionByText = options.find(opt => opt.text.toLowerCase() === ans.toLowerCase());
          if (optionByText) {
            expandedAnswers.add(optionByText.id);
          }
        });
        
        // Expand condition values (add both UUID and text versions)
        conditionVals.forEach(val => {
          expandedConditions.add(val); // Original value
          
          // Find option by text -> add UUID
          const optionByText = options.find(opt => opt.text.toLowerCase() === val.toLowerCase());
          if (optionByText) {
            expandedConditions.add(optionByText.id);
          }
          
          // Find option by UUID -> add text
          const optionById = options.find(opt => opt.id === val);
          if (optionById) {
            expandedConditions.add(optionById.text);
          }
        });
        
        // Check for any intersection
        for (const expandedAns of expandedAnswers) {
          if (expandedConditions.has(expandedAns)) {
            return true;
          }
        }
      }
      
      return false;
    };
    
    switch (operator) {
      case 'IS':
      case 'is':
      case 'CONTAINS':  
        if (isArray(conditionValue)) {
          return compareMultiSelectValues(answerArray, conditionValue.map(String));
        }
        return compareMultiSelectValues(answerArray, [String(conditionValue)]);

      case 'IS_NOT':
      case 'isNot':
      case 'DOES_NOT_CONTAINS':  
      case 'doesNotContains':
      case 'DOES_NOT_CONTAIN':
      case 'doesNotContain':   
        if (isArray(conditionValue)) {
          return !compareMultiSelectValues(answerArray, conditionValue.map(String));
        }
        return !compareMultiSelectValues(answerArray, [String(conditionValue)]);

      default:
        return false;
    }
  } catch (error) {
    console.error(`[RE] ❌ Multi-select evaluation failed for question:`, error);
    return false;
  }
}

function evaluateRatingCondition(
  operator: LOGIC_OPERATORS,
  answer: AnswerValue,
  conditionValue: unknown,
  state: RuleEngineState,
  questionId?: string,
  currentQuestionId?: string
): boolean {
  try {

    if (operator === 'IS_ANSWERED' || operator === 'isAnswered') {
      // Only check if answered for questions that come before or at current position
      if (questionId && currentQuestionId) {
        const questionIndex = state.questionSequence.indexOf(questionId);
        const currentIndex = state.questionSequence.indexOf(currentQuestionId);
        
        if (questionIndex >= currentIndex) {
          return false; // Current and future questions can't be "answered" yet
        }
      }
      
      const isAnswered = answer !== undefined && answer !== null && answer !== '' && !isNaN(Number(answer));
      return isAnswered;
    }

    if (operator === 'IS_UNANSWERED' || operator === 'isUnanswered') {
      // Only check if unanswered for questions that come before or at current position
      if (questionId && currentQuestionId) {
        const questionIndex = state.questionSequence.indexOf(questionId);
        const currentIndex = state.questionSequence.indexOf(currentQuestionId);
        
        if (questionIndex >= currentIndex) {
          return false; // Current and future questions can't be "unanswered" yet
        }
      }
      
      const isUnanswered = answer === undefined || answer === null || answer === '' || isNaN(Number(answer));
      return isUnanswered;
    }

    const answerNum = Number(answer);
    if (isNaN(answerNum)) return false;

  const getNumericValue = (val: unknown): number | null => {
    const num = Number(val);
    return isNaN(num) ? null : num;
  };

  const getNumericArray = (val: unknown): number[] | null => {
    if (!isArray(val)) return null;
    const nums = val.map(getNumericValue).filter(n => n !== null) as number[];
    return nums.length === val.length ? nums : null;
  };


  switch (operator) {
    case 'IS':
    case 'is':
    case 'CONTAINS':  
      const inVals = getNumericArray(conditionValue);
      if (inVals !== null) {
        return inVals.includes(answerNum);
      }
      if (isArray(conditionValue)) {
        return conditionValue.some(val => {
          const numVal = getNumericValue(val);
          return numVal !== null && answerNum === numVal;
        });
      }
      const singleVal = getNumericValue(conditionValue);
      return singleVal !== null && answerNum === singleVal;

    case 'IS_NOT':
    case 'isNot':
    case 'DOES_NOT_CONTAINS':  
    case 'doesNotContains':
    case 'DOES_NOT_CONTAIN':
    case 'doesNotContain':   
      const notInVals = getNumericArray(conditionValue);
      if (notInVals !== null) {
        return !notInVals.includes(answerNum);
      }
      if (isArray(conditionValue)) {
        return !conditionValue.some(val => {
          const numVal = getNumericValue(val);
          return numVal !== null && answerNum === numVal;
        });
      }
      return true;

    case 'EQUALS':
    case 'equals':
      const equalVal = getNumericValue(conditionValue);
      return equalVal !== null && answerNum === equalVal;

    

    case 'NOT_EQUALS':
    case 'notEquals':
      const notEqualVal = getNumericValue(conditionValue);
      return notEqualVal !== null && answerNum !== notEqualVal;

    case 'GREATER_THAN':
    case 'greaterThan':
      const gtVal = getNumericValue(conditionValue);
      return gtVal !== null && answerNum > gtVal;

    case 'GREATER_THAN_OR_EQUAL':
    case 'greaterThanOrEqual':
    case 'GREATER_THAN_OR_EQUAL_TO':
    case 'greaterThanOrEqualTo':
      const gteVal = getNumericValue(conditionValue);
      return gteVal !== null && answerNum >= gteVal;

    case 'LESS_THAN':
    case 'lessThan':
      const ltVal = getNumericValue(conditionValue);
      return ltVal !== null && answerNum < ltVal;

    case 'LESS_THAN_OR_EQUAL':
    case 'lessThanOrEqual':
    case 'LESS_THAN_OR_EQUAL_TO':
    case 'lessThanOrEqualTo':
      const lteVal = getNumericValue(conditionValue);
      return lteVal !== null && answerNum <= lteVal;
      
    case 'IS_EQUAL_TO':
    case 'isEqualTo':
      const equalToVal = getNumericValue(conditionValue);
      return equalToVal !== null && answerNum === equalToVal;
      
    case 'IS_NOT_EQUAL_TO':
    case 'isNotEqualTo':
      const notEqualToVal = getNumericValue(conditionValue);
      return notEqualToVal !== null && answerNum !== notEqualToVal;
      
    case 'BETWEEN':
    case 'between':
      if (isArray(conditionValue) && conditionValue.length >= 2) {
        const min = getNumericValue(conditionValue[0]);
        const max = getNumericValue(conditionValue[1]);
        return min !== null && max !== null && answerNum >= min && answerNum <= max;
      }
      return false;
      
    case 'OUTSIDE':
    case 'outside':
      if (isArray(conditionValue) && conditionValue.length >= 2) {
        const min = getNumericValue(conditionValue[0]);
        const max = getNumericValue(conditionValue[1]);
        return min !== null && max !== null && (answerNum < min || answerNum > max);
      }
      return false;
      
    case 'IN':
    case 'in':
      const inVals2 = getNumericArray(conditionValue);
      if (inVals2 !== null) {
        return inVals2.includes(answerNum);
      }
      if (isArray(conditionValue)) {
        return conditionValue.some(val => {
          const numVal = getNumericValue(val);
          return numVal !== null && answerNum === numVal;
        });
      }
      const singleInVal = getNumericValue(conditionValue);
      return singleInVal !== null && answerNum === singleInVal;
      
    case 'NOT_IN':
    case 'notIn':
      const notInVals2 = getNumericArray(conditionValue);
      if (notInVals2 !== null) {
        return !notInVals2.includes(answerNum);
      }
      if (isArray(conditionValue)) {
        return !conditionValue.some(val => {
          const numVal = getNumericValue(val);
          return numVal !== null && answerNum === numVal;
        });
      }
      const singleNotInVal = getNumericValue(conditionValue);
      return singleNotInVal === null || answerNum !== singleNotInVal;

    default:
      return false;
  }
  } catch (error) {
    console.error(`[RE] ❌ Rating evaluation failed for question:`, error);
    return false;
  }
}

function evaluateNPSCondition(
  operator: LOGIC_OPERATORS,
  answer: AnswerValue,
  conditionValue: unknown,
  state: RuleEngineState,
  questionId?: string,
  currentQuestionId?: string
): boolean {
  try {
    if (operator === 'IS_ANSWERED' || operator === 'isAnswered') {
      // Only check if answered for questions that come before or at current position
      if (questionId && currentQuestionId) {
        const questionIndex = state.questionSequence.indexOf(questionId);
        const currentIndex = state.questionSequence.indexOf(currentQuestionId);
        
        if (questionIndex >= currentIndex) {
          return false; // Current and future questions can't be "answered" yet
        }
      }
      
      const answerNum = Number(answer);
      const isAnswered = answer !== undefined && answer !== null && answer !== '' && 
        !isNaN(answerNum) && answerNum >= 0 && answerNum <= 10;
      return isAnswered;
    }

    if (operator === 'IS_UNANSWERED' || operator === 'isUnanswered') {
      // Only check if unanswered for questions that come before or at current position
      if (questionId && currentQuestionId) {
        const questionIndex = state.questionSequence.indexOf(questionId);
        const currentIndex = state.questionSequence.indexOf(currentQuestionId);
        
        if (questionIndex >= currentIndex) {
          return false; // Current and future questions can't be "unanswered" yet
        }
      }
      
      const answerNum = Number(answer);
      const isUnanswered = answer === undefined || answer === null || answer === '' || 
        isNaN(answerNum) || answerNum < 0 || answerNum > 10;
      return isUnanswered;
    }

    const answerNum = Number(answer);
    if (isNaN(answerNum) || answerNum < 0 || answerNum > 10) return false;

    let npsCategory: 'detractor' | 'passive' | 'promoter';
    if (answerNum <= 6) {
      npsCategory = 'detractor';
    } else if (answerNum <= 8) {
      npsCategory = 'passive';
    } else {
      npsCategory = 'promoter';
    }

    const conditionStr = String(conditionValue).toLowerCase();
    if (['detractor', 'passive', 'promoter'].includes(conditionStr)) {
      return conditionStr === npsCategory;
    }


    return evaluateRatingCondition(operator, answer, conditionValue, state, questionId, currentQuestionId);
  } catch (error) {
    console.error(`[RE] ❌ NPS evaluation failed for question:`, error);
    return false;
  }
}

function evaluateTextCondition(
  operator: LOGIC_OPERATORS,
  answer: AnswerValue,
  conditionValue: unknown,
  state: RuleEngineState,
  questionId?: string,
  currentQuestionId?: string
): boolean {
  try {
    if (operator === 'IS_ANSWERED' || operator === 'isAnswered') {
      // Only check if answered for questions that come before or at current position
      if (questionId && currentQuestionId) {
        const questionIndex = state.questionSequence.indexOf(questionId);
        const currentIndex = state.questionSequence.indexOf(currentQuestionId);
        
        if (questionIndex >= currentIndex) {
          return false; // Current and future questions can't be "answered" yet
        }
      }

      const isAnswered = answer !== undefined && answer !== null && String(answer).trim() !== '';
      return isAnswered;
    }

    if (operator === 'IS_UNANSWERED' || operator === 'isUnanswered') {
      // Only check if unanswered for questions that come before or at current position
      if (questionId && currentQuestionId) {
        const questionIndex = state.questionSequence.indexOf(questionId);
        const currentIndex = state.questionSequence.indexOf(currentQuestionId);
        
        if (questionIndex >= currentIndex) {
          return false; // Current and future questions can't be "unanswered" yet
        }
      }

      const isUnanswered = answer === undefined || answer === null || String(answer).trim() === '';
      return isUnanswered;
    }

    const answerStr = String(answer || '');
    
    switch (operator) {
      case 'IS':
      case 'is':
      case 'CONTAINS':  
        const searchStr = String(conditionValue || '');
        return answerStr.toLowerCase().includes(searchStr.toLowerCase());

      case 'IS_NOT':
      case 'isNot':
      case 'DOES_NOT_CONTAINS':  
      case 'doesNotContains':   
        const excludeStr = String(conditionValue || '');
        return !answerStr.toLowerCase().includes(excludeStr.toLowerCase());

      default:
        return false;
    }
  } catch (error) {
    console.error(`[RE] ❌ Text evaluation failed for question:`, error);
    return false;
  }
}

function evaluateGenericCondition(
  operator: LOGIC_OPERATORS,
  answer: AnswerValue,
  conditionValue: unknown,
  state: RuleEngineState,
  questionId?: string,
  currentQuestionId?: string
): boolean {
  try {
    if (isArray(answer)) {
      return evaluateMultiSelectCondition(operator, answer, conditionValue, state, questionId, currentQuestionId);
    }

    const answerNum = Number(answer);
    if (!isNaN(answerNum)) {
      return evaluateRatingCondition(operator, answer, conditionValue, state, questionId, currentQuestionId);
    }

    if (isString(answer)) {
      return evaluateTextCondition(operator, answer, conditionValue, state, questionId, currentQuestionId);
    }

    return false;
  } catch (error) {
    console.error(`[RE] ❌ Generic evaluation failed for question:`, error);
    return false;
  }
}

// Mixed expression evaluation
export function evaluateMixedExpression(
  state: RuleEngineState,
  expression: MixedExpression
): boolean {
  if (expression.type === 'condition') {
    const condition = expression.condition;
    const rawAnswer = state.answers[condition.questionId];
    
    let answer: AnswerValue = rawAnswer as AnswerValue;
    if (rawAnswer && typeof rawAnswer === 'object' && 'value' in rawAnswer) {
      answer = (rawAnswer as { value: AnswerValue }).value;
    }
    
    // Normalize empty strings to undefined for proper IS_UNANSWERED evaluation
    if (answer === '' || (typeof answer === 'string' && answer.trim() === '')) {
      answer = undefined;
    }
    
    // If the question is not in state.answers at all, it's undefined (unanswered)
    if (!(condition.questionId in state.answers)) {
      answer = undefined;
    }
    
    let conditionValue: any = undefined;
    if (condition.values !== undefined) {
      conditionValue = condition.values;
    } else if (condition.value !== undefined) {
      // Handle array values that come from API
      if (Array.isArray(condition.value)) {
        conditionValue = condition.value;
      } else if ((condition.operator === 'IN' || condition.operator === 'NOT_IN') && typeof condition.value === 'string' && condition.value.includes(',')) {
        conditionValue = condition.value.split(',').map(v => v.trim());
      } else {
        conditionValue = condition.value;
      }
    }
    
    const conditionObject = {
      operator: condition.operator,
      value: conditionValue
    };
    
    return evaluateCondition(state, condition.questionId, conditionObject, answer);
  }
  
  if (expression.type === 'logic') {
    const leftResult = evaluateMixedExpression(state, expression.left);
    const rightResult = evaluateMixedExpression(state, expression.right);
    
    return expression.operator === 'AND' 
      ? leftResult && rightResult
      : leftResult || rightResult;
  }
  
  return false;
}

// Compound condition evaluation - COMPLETELY REWRITTEN
export function evaluateCompoundConditions(
  state: RuleEngineState,
  conditions: AdvancedCondition[],
  conditionLogic: 'AND' | 'OR',
  currentQuestionId?: string
): boolean {
  if (conditions.length === 0) return true;
  
  
  const results = conditions.map((condition, index) => {
    // Get the answer for this question

    const rawAnswer = state.answers[condition.questionId];
    let answer: AnswerValue = rawAnswer as AnswerValue;

    
    // Handle answer objects with value property
    if (rawAnswer && typeof rawAnswer === 'object' && 'value' in rawAnswer) {
      answer = (rawAnswer as { value: AnswerValue }).value;
    }


    
    // Normalize empty strings to undefined for proper IS_UNANSWERED evaluation
    if (answer === '' || (typeof answer === 'string' && answer.trim() === '')) {
      answer = undefined;
    }
    
  
    
    
    // Get the condition value - SIMPLE AND DIRECT
    let conditionValue: any = condition.value;
    
    // If values is set, use that instead
    if (condition.values !== undefined) {
      conditionValue = condition.values;
    }
    
    // Create the condition object for evaluation
    const conditionObject = {
      operator: condition.operator,
      value: conditionValue
    };


    
    // Evaluate the condition
    const result = evaluateCondition(state, condition.questionId, conditionObject, answer, currentQuestionId);
    
    return result;
  });
  
  // Apply the logic (AND/OR)
  const finalResult = conditionLogic === 'AND' 
    ? results.every(Boolean)
    : results.some(Boolean);
    
  
  return finalResult;
}

// Advanced rule processing
export function processAdvancedRule(
  state: RuleEngineState,
  rule: AdvancedRule
): { actions: AdvancedAction[], conditionMet: boolean, otherwiseAction?: AdvancedAction } {
  if (!rule.isActive) {
    return { actions: [], conditionMet: false };
  }
  
  let conditionMet: boolean;
  
  // Use mixed expression if available, fallback to simple logic
  if (rule.ifExpression) {
    conditionMet = evaluateMixedExpression(state, rule.ifExpression);
  } else {
    conditionMet = evaluateCompoundConditions(
      state, 
      rule.ifConditions, 
      rule.conditionLogic
    );
  }
  
  if (conditionMet) {
    return { actions: rule.thenActions, conditionMet: true };
  } else {
    // Return otherwiseAction when conditions are not met
    return { 
      actions: [], 
      conditionMet: false, 
      otherwiseAction: rule.otherwiseAction 
    };
  }
}

// Helper function to recursively resolve hidden question chains (Array version)
function resolveHiddenQuestionChain(
  targetQuestionId: string,
  state: RuleEngineState,
  hiddenQuestions: string[],
  visited: Set<string> = new Set(),
  depth: number = 0
): string | null {
  // Input validation
  if (!targetQuestionId || typeof targetQuestionId !== 'string' || targetQuestionId.trim() === '') {
    return null;
  }
  
  // Maximum recursion depth protection (prevent stack overflow)
  const MAX_CHAIN_DEPTH = 100;
  if (depth > MAX_CHAIN_DEPTH) {
    return null;
  }
  
  // Prevent infinite cycles
  if (visited.has(targetQuestionId)) {
    return null;
  }
  visited.add(targetQuestionId);
  
  const targetQuestion = state.questions[targetQuestionId];
  if (!targetQuestion) {
    return null;
  }
  
  // Always return the target question regardless of visibility
  return targetQuestionId;
  
  // Target is hidden, check if it has nextQuestionId
  if (targetQuestion.config && 'nextQuestionId' in targetQuestion.config && targetQuestion.config.nextQuestionId) {
    const nextId = targetQuestion.config.nextQuestionId;
    // Validate nextQuestionId and prevent direct self-reference (Q4 -> Q4)
    if (typeof nextId === 'string' && nextId.trim() !== '' && nextId !== targetQuestionId) {
      // Recursively resolve the chain with depth increment
      return resolveHiddenQuestionChain(nextId, state, hiddenQuestions, visited, depth + 1);
    }
    
    // If self-reference detected (Q4 -> Q4), fall through to sequential logic
    if (nextId === targetQuestionId) {
      // Self-reference blocked - treat as if no nextQuestionId exists
    }
  }
  
  // No nextQuestionId, find next visible in sequence after target
  const targetIndex = state.questionSequence.indexOf(targetQuestionId);
  if (targetIndex !== -1) {
    for (let i = targetIndex + 1; i < state.questionSequence.length; i++) {
      const candidateId = state.questionSequence[i];
      if (!hiddenQuestions.includes(candidateId)) {
        return candidateId;
      }
    }
  }
  
  // No visible questions found
  return null;
}

// Helper function to recursively resolve hidden question chains (Set version for executeRuleEngine)
function resolveHiddenQuestionChainSet(
  targetQuestionId: string,
  questions: Record<string, any>,
  questionSequence: string[],
  hiddenQuestions: Set<string>,
  visited: Set<string> = new Set(),
  depth: number = 0
): string | null {
  // Input validation
  if (!targetQuestionId || typeof targetQuestionId !== 'string' || targetQuestionId.trim() === '') {
    return null;
  }
  
  // Maximum recursion depth protection (prevent stack overflow)
  const MAX_CHAIN_DEPTH = 100;
  if (depth > MAX_CHAIN_DEPTH) {
    return null;
  }
  
  // Prevent infinite cycles
  if (visited.has(targetQuestionId)) {
    return null;
  }
  visited.add(targetQuestionId);
  
  const targetQuestion = questions[targetQuestionId];
  if (!targetQuestion) {
    return null;
  }
  
  // Always return the target question regardless of visibility
  return targetQuestionId;
  
  // Target is hidden, check if it has nextQuestionId
  if (targetQuestion.config && 'nextQuestionId' in targetQuestion.config && targetQuestion.config.nextQuestionId) {
    const nextId = targetQuestion.config.nextQuestionId;
    // Validate nextQuestionId and prevent direct self-reference (Q4 -> Q4)
    if (typeof nextId === 'string' && nextId.trim() !== '' && nextId !== targetQuestionId) {
      // Recursively resolve the chain with depth increment
      return resolveHiddenQuestionChainSet(nextId, questions, questionSequence, hiddenQuestions, visited, depth + 1);
    }
    
    
  }
  
  // No nextQuestionId, find next visible in sequence after target
  const targetIndex = questionSequence.indexOf(targetQuestionId);
  if (targetIndex !== -1) {
    for (let i = targetIndex + 1; i < questionSequence.length; i++) {
      const candidateId = questionSequence[i];
      if (!hiddenQuestions.has(candidateId)) {
        return candidateId;
      }
    }
  }
  
  // No visible questions found
  return null;
}

// Advanced next question logic
export function getAdvancedNextQuestion(
  state: RuleEngineState,
  currentQuestionId: string,
  surveyRules: AdvancedRule[] = []
): { 
  questionId: string | null; 
  isTerminal: boolean; 
  hiddenQuestions: string[]; 
  shownQuestions: string[];
  navigationMethod: 'JUMP_TO' | 'NEXT_QUESTION_ID' | 'SHOW_HIDE' | 'SEQUENTIAL' | 'OTHERWISE';
} {
  try {
    const rawCurrentAnswer = state.answers[currentQuestionId];
    let currentAnswer = rawCurrentAnswer;
    if (rawCurrentAnswer && typeof rawCurrentAnswer === 'object' && 'value' in rawCurrentAnswer) {
      currentAnswer = (rawCurrentAnswer as { value: any }).value;
    }
    
    // Normalize empty strings to undefined for proper IS_UNANSWERED evaluation
    if (currentAnswer === '' || (typeof currentAnswer === 'string' && currentAnswer.trim() === '')) {
      currentAnswer = undefined;
    }
    
    const activeRules = surveyRules
      .filter(rule => rule.isActive)
      .sort((a, b) => b.priority - a.priority);
      
    let hiddenQuestions: string[] = [];
    let shownQuestions: string[] = [];
    let anyConditionMet = false;
    
    const hiddenQuestionsSet = new Set<string>();
    const shownQuestionsSet = new Set<string>();
    let jumpTarget: string | null = null;
    let shouldEndSurvey = false;
    let otherwiseActions: Array<{ action: AdvancedAction, priority: number, ruleId: string }> = [];
    
    // Single pass: Process all rules and collect actions
    for (const rule of activeRules) {
      const { actions, conditionMet, otherwiseAction } = processAdvancedRule(state, rule);
      
      if (conditionMet) {
        anyConditionMet = true;
        
        for (const action of actions) {
          switch (action.type) {
            case 'HIDE':
            case 'hide':
              if (action.questionIds) {
                action.questionIds
                  .filter(id => state.questions[id])
                  .forEach(id => hiddenQuestionsSet.add(id));
              }
              break;
              
            case 'SHOW':
            case 'show':
              if (action.questionIds) {
                action.questionIds
                  .filter(id => state.questions[id])
                  .forEach(id => shownQuestionsSet.add(id));
              }
              break;
              
            case 'JUMP_TO':
            case 'jumpTo':
              if (action.questionIds && action.questionIds.length > 0 && !jumpTarget && !shouldEndSurvey) {
                const targetId = action.questionIds[0];
                if (targetId === 'END' || targetId === 'END_SURVEY') {
                  shouldEndSurvey = true;
                } else if (state.questions[targetId]) {
                  jumpTarget = targetId;
                }
              }
              break;
              
            case 'END_SURVEY':
            case 'endSurvey':
              if (!shouldEndSurvey) {
                shouldEndSurvey = true;
              }
              break;
          }
        }
      }
      
      // Collect otherwiseAction from ALL rules (we'll filter later)
      if (otherwiseAction && 
          (!rule.sourceQuestionId || currentQuestionId === rule.sourceQuestionId)) {
        otherwiseActions.push({ 
          action: otherwiseAction, 
          priority: rule.priority || 0, 
          ruleId: rule.ruleId 
        });
      }
    }
    
    // Process otherwiseActions if no conditions were met
    if (!anyConditionMet && otherwiseActions.length > 0) {
      // Sort by priority (highest first) and take the first one
      otherwiseActions.sort((a, b) => b.priority - a.priority);
      const highestPriorityOtherwise = otherwiseActions[0];
      
      // Only allow JUMP_TO and END_SURVEY for OTHERWISE (single destination rule)
      switch (highestPriorityOtherwise.action.type) {
        case 'JUMP_TO':
        case 'jumpTo':
          if (highestPriorityOtherwise.action.questionIds && highestPriorityOtherwise.action.questionIds.length > 0) {
            const targetId = highestPriorityOtherwise.action.questionIds[0];
            if (targetId === 'END' || targetId === 'END_SURVEY') {
              // Direct return for otherwiseAction END - bypass all other logic
              return { 
                questionId: null, 
                isTerminal: true, 
                hiddenQuestions: Array.from(hiddenQuestionsSet), 
                shownQuestions: Array.from(shownQuestionsSet) 
              };
            } else {
              // Direct return for otherwiseAction JUMP_TO - bypass all visibility checks
              return { 
                questionId: targetId, 
                isTerminal: false, 
                hiddenQuestions: Array.from(hiddenQuestionsSet), 
                shownQuestions: Array.from(shownQuestionsSet) 
              };
            }
          }
          break;
          
        case 'END_SURVEY':
        case 'endSurvey':
          // Direct return for otherwiseAction END_SURVEY
          return { 
            questionId: null, 
            isTerminal: true, 
            hiddenQuestions: Array.from(hiddenQuestionsSet), 
            shownQuestions: Array.from(shownQuestionsSet),
            navigationMethod: 'OTHERWISE'
          };
      }
    }
    
    // Process navigation actions after collecting all visibility changes
    if (shouldEndSurvey) {
      return { 
        questionId: null, 
        isTerminal: true, 
        hiddenQuestions: Array.from(hiddenQuestionsSet), 
        shownQuestions: Array.from(shownQuestionsSet),
        navigationMethod: 'JUMP_TO'
      };
    }
    
    if (jumpTarget) {
      // Direct jump without visibility checks
      return { 
        questionId: jumpTarget, 
        isTerminal: false, 
        hiddenQuestions: Array.from(hiddenQuestionsSet), 
        shownQuestions: Array.from(shownQuestionsSet),
        navigationMethod: 'JUMP_TO'
      };
    }
    
    // If conditions were met but no jump target, skip hidden questions only
    if (anyConditionMet) {
      const currentIndex = state.questionSequence.indexOf(currentQuestionId);
      
      // Skip hidden questions, continue sequential navigation
      if (hiddenQuestionsSet.size > 0) {
        const hiddenQuestionsArray = Array.from(hiddenQuestionsSet);
        
        // Find next question that is NOT hidden
        for (let i = currentIndex + 1; i < state.questionSequence.length; i++) {
          const candidateId = state.questionSequence[i];
          if (!hiddenQuestionsArray.includes(candidateId)) {
            return {
              questionId: candidateId,
              isTerminal: false,
              hiddenQuestions: Array.from(hiddenQuestionsSet),
              shownQuestions: Array.from(shownQuestionsSet),
              navigationMethod: 'SHOW_HIDE'
            };
          }
        }
      }
    }
    
    hiddenQuestions = Array.from(hiddenQuestionsSet);
    shownQuestions = Array.from(shownQuestionsSet);
    
    // 2.) nextQuestionId from config
    const currentQuestion = state.questions[currentQuestionId];
    if (currentQuestion && currentQuestion.config) {
      const configNextId = currentQuestion.config.nextQuestionId;
      const configIsTerminal = Boolean(currentQuestion.config.isTerminal || currentQuestion.logic?.isTerminal);
      
      // Check if Layer 2 config exists (either nextQuestionId or isTerminal set)
      const hasLayer2Config = 'nextQuestionId' in currentQuestion.config || configIsTerminal;
      
      if (hasLayer2Config) {
        // Case 1 & 2: isTerminal = true → END_SURVEY (regardless of nextQuestionId value)
        if (configIsTerminal) {
          return {
            questionId: null,
            isTerminal: true,
            hiddenQuestions,
            shownQuestions,
            navigationMethod: 'NEXT_QUESTION_ID'
          };
        }
        
        // Case 3: nextQuestionId === null && isTerminal === false → Fallback to Layer 3
        if (configNextId === null || configNextId === undefined) {
          // Don't return here - let it fall through to Layer 3
        } else {
          // Case 4: nextQuestionId !== null && isTerminal === false → Normal navigation
          return {
            questionId: configNextId,
            isTerminal: false,
            hiddenQuestions,
            shownQuestions,
            navigationMethod: 'NEXT_QUESTION_ID'
          };
        }
      }
    }
    
    // 3.) Sequential logic final fallback
    const currentIndex = state.questionSequence.indexOf(currentQuestionId);
    const nextIndex = currentIndex + 1;
    let finalQuestionId = nextIndex < state.questionSequence.length ? state.questionSequence[nextIndex] : null;
    
    // Skip hidden questions
    if (finalQuestionId && hiddenQuestions.includes(finalQuestionId)) {
      for (let i = currentIndex + 1; i < state.questionSequence.length; i++) {
        const candidateId = state.questionSequence[i];
        if (!hiddenQuestions.includes(candidateId)) {
          finalQuestionId = candidateId;
          break;
        }
      }
      // If no visible question found after current, end survey
      if (finalQuestionId && hiddenQuestions.includes(finalQuestionId)) {
        finalQuestionId = null;
      }
    }
    
    return {
      questionId: finalQuestionId,
      isTerminal: finalQuestionId === null,
      hiddenQuestions,
      shownQuestions,
      navigationMethod: 'SEQUENTIAL'
    };
    
  } catch {
    const currentIndex = state.questionSequence.indexOf(currentQuestionId);
    const nextIndex = currentIndex + 1;
    const nextQuestionId = nextIndex < state.questionSequence.length ? state.questionSequence[nextIndex] : null;
    return {
      questionId: nextQuestionId,
      isTerminal: nextQuestionId === null,
      hiddenQuestions: [],
      shownQuestions: [],
      navigationMethod: 'SEQUENTIAL'
    };
  }
}

// Utility functions
export function getNextQuestion(
  state: RuleEngineState,
  currentQuestionId: string
): { questionId: string | null; isTerminal: boolean } {
  const currentIndex = state.questionSequence.indexOf(currentQuestionId);
  const nextIndex = currentIndex + 1;
  const nextQuestionId = nextIndex < state.questionSequence.length ? state.questionSequence[nextIndex] : null;
  
  return {
    questionId: nextQuestionId,
    isTerminal: nextQuestionId === null
  };
}

export function getPreviousQuestion(state: RuleEngineState, currentQuestionId: string): string | null {
  try {
    const currentPathIndex = state.currentPath.indexOf(currentQuestionId);
    
    if (currentPathIndex > 0) {
      return state.currentPath[currentPathIndex - 1];
    }

    const currentIndex = state.questionSequence.indexOf(currentQuestionId);
    if (currentIndex > 0) {
      return state.questionSequence[currentIndex - 1];
    }

    return null;
  } catch {
    return null;
  }
}

export function isLastQuestion(state: RuleEngineState, questionId: string): boolean {
  try {
    const question = state.questions[questionId];
    if (!question) return false;

    const logic = question.logic || {};
    const config = question.config || {};

    if (logic.isTerminal || (isObject(config) && safeGet(config, 'isTerminal', false))) {
      return true;
    }

    const nextResult = getNextQuestion(state, questionId);
    return nextResult.isTerminal || !nextResult.questionId;
  } catch {
    return false;
  }
}

export function getVisibleQuestionSequence(state: RuleEngineState): string[] {
  try {
    if (state.questionSequence.length === 0) return [];

    const result: string[] = [];
    const seen = new Set<string>();
    let currentId = state.questionSequence[0];
    let maxIterations = 200;

    result.push(currentId);
    seen.add(currentId);

    while (currentId && maxIterations-- > 0) {
      const tempState = {
        ...state,
        answers: Object.fromEntries(
          Object.entries(state.answers).filter(([key]) => result.includes(key))
        )
      };

      const { questionId, isTerminal } = getNextQuestion(tempState, currentId);

      if (!questionId || isTerminal || seen.has(questionId)) {
        break;
      }

      result.push(questionId);
      seen.add(questionId);
      currentId = questionId;
    }

    return result;
  } catch {
    return state.questionSequence;
  }
}

export function calculateProgress(
  state: RuleEngineState,
  currentQuestionId: string
): { currentQuestion: number; totalQuestions: number; percentage: number } {
  try {
    const visibleSequence = getVisibleQuestionSequence(state);
    const currentIndex = visibleSequence.indexOf(currentQuestionId);

    if (currentIndex === -1 || visibleSequence.length === 0) {
      return { currentQuestion: 0, totalQuestions: 0, percentage: 0 };
    }

    return {
      currentQuestion: currentIndex + 1,
      totalQuestions: visibleSequence.length,
      percentage: Math.round(((currentIndex + 1) / visibleSequence.length) * 100)
    };
  } catch {
    return { currentQuestion: 1, totalQuestions: 1, percentage: 100 };
  }
}

// Rule validation
export function validateOperatorForQuestionType(
  questionType: QuestionType,
  operator: LOGIC_OPERATORS
): boolean {
  const textChoiceOperators = ['IS', 'is', 'IS_NOT', 'isNot', 'IS_ANSWERED', 'isAnswered', 'IS_UNANSWERED', 'isUnanswered'];
  const numericOperators = ['EQUALS', 'equals', 'NOT_EQUALS', 'notEquals', 'GREATER_THAN', 'greaterThan', 'GREATER_THAN_OR_EQUAL', 'greaterThanOrEqual', 'LESS_THAN', 'lessThan', 'LESS_THAN_OR_EQUAL', 'lessThanOrEqual'];
  const answerStatusOperators = ['IS_ANSWERED', 'isAnswered', 'IS_UNANSWERED', 'isUnanswered'];
  
  switch (questionType) {
    case 'text':
      return answerStatusOperators.includes(operator);
    case 'single_select':
    case 'multi_select':
    case 'dropdown':
    case 'image':
      return textChoiceOperators.includes(operator);
    case 'nps':
    case 'rating':
      return [...textChoiceOperators, ...numericOperators].includes(operator);
    default:
      return textChoiceOperators.includes(operator);
  }
}

export function validateAction(
  state: RuleEngineState,
  action: AdvancedAction
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!action.type) {
    errors.push('Action type is required');
  }
  
  if (action.questionIds && action.questionIds.length > 0) {
    for (const questionId of action.questionIds) {
      if (!state.questions[questionId]) {
        errors.push(`Question ID ${questionId} not found in survey`);
      }
    }
  }
  
  switch (action.type) {
    case 'JUMP_TO':
    case 'jumpTo':
      if (!action.questionIds || action.questionIds.length === 0) {
        errors.push('JUMP_TO action requires at least one question ID');
      }
      break;
      
    case 'SHOW':
    case 'show':
    case 'HIDE':
    case 'hide':
      if (!action.questionIds || action.questionIds.length === 0) {
        errors.push(`${action.type} action requires at least one question ID`);
      }
      break;
      
    case 'END_SURVEY':
    case 'endSurvey':
      break;
      
    default:
      errors.push(`Unknown action type: ${action.type}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

export function validateRule(
  state: RuleEngineState,
  rule: AdvancedRule
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!rule.ruleId || !rule.surveyId) {
    errors.push('Rule ID and Survey ID are required');
  }
  
  if (!rule.ifConditions || rule.ifConditions.length === 0) {
    errors.push('Rule must have at least one condition');
  }
  
  if (!rule.thenActions || rule.thenActions.length === 0) {
    errors.push('Rule must have at least one action');
  }
  
  for (const condition of rule.ifConditions) {
    if (!condition.questionId) {
      errors.push('Condition must have a question ID');
      continue;
    }
    
    if (!state.questions[condition.questionId]) {
      errors.push(`Condition references non-existent question: ${condition.questionId}`);
      continue;
    }
    
    const question = state.questions[condition.questionId];
    if (!validateOperatorForQuestionType(question.type, condition.operator)) {
      errors.push(`Operator '${condition.operator}' not supported for question type '${question.type}'`);
    }
  }
  
  for (const action of rule.thenActions) {
    const actionValidation = validateAction(state, action);
    if (!actionValidation.isValid) {
      errors.push(...actionValidation.errors);
    }
  }
  
  // Validate otherwiseAction if it exists
  if (rule.otherwiseAction) {
    const otherwiseValidation = validateAction(state, rule.otherwiseAction);
    if (!otherwiseValidation.isValid) {
      errors.push(...otherwiseValidation.errors.map(err => `Otherwise action: ${err}`));
    }
    
    // OTHERWISE must be single destination only (JUMP_TO or END_SURVEY)
    if (rule.otherwiseAction.type !== 'JUMP_TO' && 
        rule.otherwiseAction.type !== 'jumpTo' && 
        rule.otherwiseAction.type !== 'END_SURVEY' && 
        rule.otherwiseAction.type !== 'endSurvey') {
      errors.push('Otherwise action must be JUMP_TO or END_SURVEY (single destination only)');
    }
    
    // If JUMP_TO, must have exactly one question ID
    if ((rule.otherwiseAction.type === 'JUMP_TO' || rule.otherwiseAction.type === 'jumpTo') &&
        (!rule.otherwiseAction.questionIds || rule.otherwiseAction.questionIds.length !== 1)) {
      errors.push('Otherwise JUMP_TO action must have exactly one question ID');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

// Rule sanitization
/**
 * Check if a specific question is visible right now based on current answers
 * Fast alternative to executeRuleEngine for single question visibility
 */
// export const isQuestionVisibleNow = (
//   targetQuestionId: string,
//   currentAnswers: Record<string, any>,
//   rules: any[],
//   questionsById: Record<string, Question>
// ): boolean => {
//   // Find only rules that affect this specific question
//   const relevantRules = rules;
  
//   console.log("relevantRules",targetQuestionId,currentAnswers,relevantRules);


//   let isShownByRules = false;
//   let isHiddenByRules = false;
  
//   // Evaluate only relevant rules with current answers
//   for (const rule of relevantRules) {
//     console.log(`[VISIBILITY] Evaluating rule for ${targetQuestionId}:`, {
//       ruleId: rule.id,
//       conditions: rule.conditions,
//       availableAnswers: Object.keys(currentAnswers),
//       answerValues: currentAnswers
//     });
    
//     if (rule.ifConditions && evaluateCompoundConditions(
//       { questions: questionsById, questionSequence: [], answers: currentAnswers, currentPath: [] },
//       rule.ifConditions,
//       rule.conditionLogic || 'AND'
//     )) {
//       console.log(`[VISIBILITY] Rule ${rule.id} conditions PASSED for ${targetQuestionId}`);
      
//       for (const action of rule.thenActions) {
//         console.log("allActions",targetQuestionId,rule,action);
//         if (action.questionIds?.includes(targetQuestionId)) {
//           console.log("targetQuestionId",targetQuestionId,action);
//           if (action.type === 'SHOW' || action.type === 'show') {
//             isShownByRules = true;
//             console.log(`[VISIBILITY] Rule ${rule.id} SHOWS ${targetQuestionId}`);
//           }
//           if (action.type === 'HIDE' || action.type === 'hide') {
//             isHiddenByRules = true;
//             console.log(`[VISIBILITY] Rule ${rule.id} HIDES ${targetQuestionId}`);
//           }
//         }
//       }
//     } else {
//       console.log(`[VISIBILITY] Rule ${rule.id} conditions FAILED for ${targetQuestionId}`);
//     }
//   }

//   console.log("derfgerfeferferftewrf",targetQuestionId,isShownByRules,isHiddenByRules);
  
//   // idiot what if both variables are true ?
//   if (isShownByRules && isHiddenByRules) {
//     console.log("derfgerfeferferftewrf",targetQuestionId,isShownByRules,isHiddenByRules);
//     return false;
//   }

//   // Apply visibility priority: Rules > Config > Default
//   if (isShownByRules) return true;
//   if (isHiddenByRules) return false;
  
//   // Fallback to config
//   const question = questionsById[targetQuestionId];
//   return question?.config?.isVisible !== false;
// };


export const isQuestionVisibleNow = (
  targetQuestionId: string,
  currentAnswers: Record<string, any>,
  rules: any[],
  questionsById: Record<string, Question>,
  currentQuestionId?: string,
  questionSequence?: string[]
): boolean => {
  // Find only rules that affect this specific question
  const relevantRules = rules;

  let mostRecentAction: { type: string; date: Date } | null = null;

  // Evaluate only relevant rules with current answers
  for (const rule of relevantRules) {

    if (rule.ifConditions && evaluateCompoundConditions(
      { questions: questionsById, questionSequence: questionSequence || [], answers: currentAnswers, currentPath: [] },
      rule.ifConditions,
      rule.conditionLogic || 'AND',
      currentQuestionId
    )) {
      for (const action of rule.thenActions) {
        if (action.questionIds?.includes(targetQuestionId)) {

          const ruleDate = new Date(rule.createdAt || rule.updatedAt || 0);

          if ((action.type === 'SHOW' || action.type === 'show' ||
               action.type === 'HIDE' || action.type === 'hide') &&
              (!mostRecentAction || ruleDate > mostRecentAction.date)) {
            mostRecentAction = { type: action.type, date: ruleDate };
          }
        }
      }
    }
  }

  // Apply most recent rule's decision
  if (mostRecentAction) {
    const isVisible = mostRecentAction.type === 'SHOW' || mostRecentAction.type === 'show';
    return isVisible;
  }

  // Fallback to config
  const question = questionsById[targetQuestionId];
  return question?.config?.isVisible !== false;
};

export function sanitizeRules(rules: any[]): AdvancedRule[] {
  if (!Array.isArray(rules)) return [];
  
  return rules
    .filter(rule => rule && typeof rule === 'object')
    .map(rule => {
      return {
        ruleId: rule.ruleId || `rule-${Date.now()}-${Math.random()}`,
        surveyId: rule.surveyId || '',
        ruleName: rule.ruleName || rule.ruleId,
        priority: Number(rule.priority) || 0,
        isActive: Boolean(rule.isActive !== false),
        sourceQuestionId: rule.sourceQuestionId || null,
        ifConditions: Array.isArray(rule.ifConditions) ? rule.ifConditions.map((condition: any) => {
          // SIMPLE ARRAY FLATTENING - NO COMPLEX LOGIC
          let processedValue = condition.value;
          let processedValues = condition.values;
          
          // If value is nested array like [["5"]], flatten to ["5"]
          if (Array.isArray(condition.value) && condition.value.length > 0 && Array.isArray(condition.value[0])) {
            processedValue = condition.value[0];
          }
          
          // If values is nested array, flatten it too
          if (Array.isArray(condition.values) && condition.values.length > 0 && Array.isArray(condition.values[0])) {
            processedValues = condition.values[0];
          }
          
          return {
            questionId: condition.questionId,
            operator: normalizeOperator(condition.operator),
            value: processedValue,
            values: processedValues
          };
        }) : [],
        conditionLogic: (rule.conditionLogic === 'OR' ? 'OR' : 'AND') as 'AND' | 'OR',
        thenActions: Array.isArray(rule.thenActions) ? rule.thenActions.map((action: any) => ({
          type: action.type,
          questionIds: action.questionIds || []
        })) : [],
        otherwiseAction: rule.otherwiseAction ? {
          type: rule.otherwiseAction.type,
          questionIds: rule.otherwiseAction.questionIds || []
        } : undefined
      };
    })
    .filter(rule => rule.ruleId && rule.surveyId);
}

/**
 * Rule engine execution with priority-based question visibility
 * PRIORITY ORDER:
 * 1. Server config (isVisible: false) - ALWAYS hidden
 * 2. Rules (HIDE/SHOW actions) - Dynamic based on conditions
 * 3. Default visibility (isVisible: true or undefined)
 */
export function executeRuleEngine(
  rules: AdvancedRule[],
  answers: Record<string, any>,
  currentQuestionId: string,
  questionSequence: string[],
  questions: Record<string, any> = {}
): {
  nextQuestionId: string | null;
  isTerminal: boolean;
  hiddenQuestions: Set<string>;
  shownQuestions: Set<string>;
  appliedRules: string[];
  wasJumpTo: boolean;
  jumpToFromQuestionId: string | null;
  navigationMethod: 'JUMP_TO' | 'NEXT_QUESTION_ID' | 'SHOW_HIDE' | 'SEQUENTIAL' | 'OTHERWISE';
} {
  try {
    // Input validation
    if (!Array.isArray(rules)) {
      return {
        nextQuestionId: null,
        isTerminal: true,
        hiddenQuestions: new Set(),
        shownQuestions: new Set(),
        appliedRules: [],
        wasJumpTo: false,
        jumpToFromQuestionId: null,
        navigationMethod: 'SEQUENTIAL'
      };
    }
    
    if (!Array.isArray(questionSequence)) {
      return {
        nextQuestionId: null,
        isTerminal: true,
        hiddenQuestions: new Set(),
        shownQuestions: new Set(),
        appliedRules: [],
        wasJumpTo: false,
        jumpToFromQuestionId: null,
        navigationMethod: 'SEQUENTIAL'
      };
    }
    
    if (!currentQuestionId || typeof currentQuestionId !== 'string') {
      return {
        nextQuestionId: null,
        isTerminal: true,
        hiddenQuestions: new Set(),
        shownQuestions: new Set(),
        appliedRules: [],
        wasJumpTo: false,
        jumpToFromQuestionId: null,
        navigationMethod: 'SEQUENTIAL'
      };
    }
    
    // Normalize answers to handle empty strings properly
    const normalizedAnswers: Record<string, any> = {};
    Object.entries(answers || {}).forEach(([key, value]) => {
      let normalizedValue = value;
      
      // Handle answer objects with value property
      if (value && typeof value === 'object' && 'value' in value) {
        normalizedValue = (value as { value: any }).value;
      }
      
      // Normalize empty strings to undefined for proper IS_UNANSWERED evaluation
      if (normalizedValue === '' || (typeof normalizedValue === 'string' && normalizedValue.trim() === '')) {
        normalizedValue = undefined;
      }
      
      // Only add to normalized answers if it's not undefined (completely remove empty answers)
      if (normalizedValue !== undefined) {
        normalizedAnswers[key] = normalizedValue;
      }
      
    });
    
    const state: RuleEngineState = {
      questions,
      questionSequence,
      answers: normalizedAnswers,
      currentPath: [currentQuestionId],
    };
    
    // Initialize hiddenQuestions with server-hidden questions (isVisible: false)
    const hiddenQuestions = new Set<string>();
    const shownQuestions = new Set<string>();
    
    // Add questions hidden by server config (isVisible: false)
    // PRIORITY: Server config (isVisible: false) → Rules (HIDE/SHOW actions)
    if (questions && Object.keys(questions).length > 0) {
      Object.values(questions).forEach(question => {
        if (question.config?.isVisible === false) {
          hiddenQuestions.add(question.questionId);
        }
      });
    }
    
    const appliedRules: string[] = [];
    let wasJumpTo = false;
    let jumpToFromQuestionId: string | null = null;
    
    let otherwiseActions: Array<{ action: AdvancedAction, priority: number, ruleId: string }> = [];
    let anyConditionMet = false;
    
    // First pass: Process all rules and determine if ANY condition was met
    for (const rule of rules) {
      const { actions, conditionMet, otherwiseAction } = processAdvancedRule(state, rule);

      if (conditionMet) {
        anyConditionMet = true;
        appliedRules.push(rule.ruleId);
        
        for (const action of actions) {
          switch (action.type) {
            case 'HIDE':
            case 'hide':
              if (action.questionIds) {
                action.questionIds.forEach(id => {
                  hiddenQuestions.add(id);
                  shownQuestions.delete(id); // Remove from shown if it was there
                });
              }
              break;
              
            case 'SHOW':
            case 'show':
              if (action.questionIds) {
                action.questionIds.forEach(id => {
                  shownQuestions.add(id);
                  hiddenQuestions.delete(id); // Remove from hidden if it was there
                });
              }
              break;
              
            case 'JUMP_TO':
            case 'jumpTo':
              // Collect JUMP_TO for later processing - don't return immediately
              break;
              
            case 'END_SURVEY':
            case 'endSurvey':
              // Collect END_SURVEY for later processing - don't return immediately
              break;
          }
        }
      }
      
      // Collect otherwiseAction from ALL rules (we'll filter later)
      if (otherwiseAction && 
          (!rule.sourceQuestionId || currentQuestionId === rule.sourceQuestionId)) {
        otherwiseActions.push({ 
          action: otherwiseAction, 
          priority: rule.priority || 0, 
          ruleId: rule.ruleId 
        });
      }
    }
    
    // Process otherwiseActions if no conditions were met
    if (!anyConditionMet && otherwiseActions.length > 0) {
      // Sort by priority (highest first) and take the first one
      otherwiseActions.sort((a, b) => b.priority - a.priority);
      const highestPriorityOtherwise = otherwiseActions[0];
      
      // Only allow JUMP_TO and END_SURVEY for OTHERWISE (single destination rule)
      switch (highestPriorityOtherwise.action.type) {
        case 'JUMP_TO':
        case 'jumpTo':
          if (highestPriorityOtherwise.action.questionIds && highestPriorityOtherwise.action.questionIds.length > 0) {
            const targetId = highestPriorityOtherwise.action.questionIds[0];
            if (targetId === 'END' || targetId === 'END_SURVEY') {
              return {
                nextQuestionId: null,
                isTerminal: true,
                hiddenQuestions,
                shownQuestions,
                appliedRules: [highestPriorityOtherwise.ruleId],
                wasJumpTo: true,
                jumpToFromQuestionId: currentQuestionId,
                navigationMethod: 'OTHERWISE'
              };
            } else if (questions[targetId]) {
              return {
                nextQuestionId: targetId,
                isTerminal: false,
                hiddenQuestions,
                shownQuestions,
                appliedRules: [highestPriorityOtherwise.ruleId],
                wasJumpTo: true,
                jumpToFromQuestionId: currentQuestionId,
                navigationMethod: 'OTHERWISE'
              };
            }
          }
          break;
          
        case 'END_SURVEY':
        case 'endSurvey':
          return {
            nextQuestionId: null,
            isTerminal: true,
            hiddenQuestions,
            shownQuestions,
            appliedRules: [highestPriorityOtherwise.ruleId],
            wasJumpTo: false,
            jumpToFromQuestionId: null,
            navigationMethod: 'OTHERWISE'
          };
      }
    }
    
    let nextQuestionId: string | null = null;
    let isTerminal = false;
    
    // 1.) Process JUMP_TO actions after collecting all rule effects
    for (const rule of rules) {
      if (appliedRules.includes(rule.ruleId)) {
        for (const action of rule.thenActions) {
          if ((action.type === 'JUMP_TO' || action.type === 'jumpTo') && action.questionIds && action.questionIds.length > 0) {
            // Only execute JUMP_TO if currentQuestionId matches sourceQuestionId
            if (rule.sourceQuestionId && currentQuestionId !== rule.sourceQuestionId) {
              continue;
            }
            // Direct jump without visibility checks
            nextQuestionId = action.questionIds[0];
            wasJumpTo = true;
            jumpToFromQuestionId = currentQuestionId;
            if (nextQuestionId === 'END') {
              isTerminal = true;
              nextQuestionId = null;
            }
          }
        }
      }
    }
    
    // 1.5.) SHOW rules only affect visibility, not navigation - continue sequentially
    if (!nextQuestionId && !isTerminal && anyConditionMet) {
      const currentIndex = questionSequence.indexOf(currentQuestionId);
      
      // Skip hidden questions, continue sequential navigation
      if (!nextQuestionId && hiddenQuestions.size > 0) {
        const hiddenQuestionsArray = Array.from(hiddenQuestions);
        
        // Find next question that is NOT hidden
        for (let i = currentIndex + 1; i < questionSequence.length; i++) {
          const candidateId = questionSequence[i];
          if (!hiddenQuestionsArray.includes(candidateId)) {
            nextQuestionId = candidateId;
            break;
          }
        }
      }
    }
  
    if (!nextQuestionId && !isTerminal) {
      for (const rule of rules) {
        if (appliedRules.includes(rule.ruleId)) {
          for (const action of rule.thenActions) {
            if (action.type === 'END_SURVEY' || action.type === 'endSurvey') {
              isTerminal = true;
              break;
            }
          }
          if (isTerminal) break;
        }
      }
    }
    
    // 2.) nextQuestionId from config
    if (!nextQuestionId && !isTerminal) {
      const currentQuestion = questions[currentQuestionId];
      if (currentQuestion && currentQuestion.config) {
        const configNextId = currentQuestion.config.nextQuestionId;
        const configIsTerminal = Boolean(currentQuestion.config.isTerminal || currentQuestion.logic?.isTerminal);
        
        // Check if Layer 2 config exists (either nextQuestionId or isTerminal set)
        const hasLayer2Config = 'nextQuestionId' in currentQuestion.config || configIsTerminal;
        
        if (hasLayer2Config) {
          // Case 1 & 2: isTerminal = true → END_SURVEY (regardless of nextQuestionId value)
          if (configIsTerminal) {
            return {
              nextQuestionId: null,
              isTerminal: true,
              hiddenQuestions,
              shownQuestions,
              appliedRules: ['config-terminal'],
              wasJumpTo: false,
              jumpToFromQuestionId: null,
              navigationMethod: 'NEXT_QUESTION_ID'
            };
          }
          
          // Case 3: nextQuestionId === null && isTerminal === false → Fallback to Layer 3
          if (configNextId === null || configNextId === undefined) {
            // Don't return here - let it fall through to Layer 3
          } else {
            // Case 4: nextQuestionId !== null && isTerminal === false → Normal navigation
            return {
              nextQuestionId: configNextId,
              isTerminal: false,
              hiddenQuestions,
              shownQuestions,
              appliedRules: ['config-next-direct'],
              wasJumpTo: false,
              jumpToFromQuestionId: null,
              navigationMethod: 'NEXT_QUESTION_ID'
            };
          }
        }
      }
    }
    
    // 3.) Sequential logic final fallback
    if (!nextQuestionId && !isTerminal) {
      const currentIndex = questionSequence.indexOf(currentQuestionId);
      if (currentIndex >= 0 && currentIndex < questionSequence.length - 1) {
        // Find the next visible question (skip hidden ones)
        let nextIndex = currentIndex + 1;
        while (nextIndex < questionSequence.length) {
          const candidateQuestionId = questionSequence[nextIndex];
          if (!hiddenQuestions.has(candidateQuestionId)) {
            nextQuestionId = candidateQuestionId;
            break;
            
          }
          nextIndex++;
        }
        
        // If no visible question found, we're at the end
        if (!nextQuestionId) {
          // Check if this means the survey should end
          const remainingQuestions = questionSequence.slice(currentIndex + 1);
          const allHidden = remainingQuestions.every(id => hiddenQuestions.has(id));
          if (allHidden) {
            isTerminal = true;
          }
        }
      }
    }
    
    
    return {
      nextQuestionId,
      isTerminal,
      hiddenQuestions,
      shownQuestions,
      appliedRules,
      wasJumpTo,
      jumpToFromQuestionId,
      navigationMethod: nextQuestionId ? (anyConditionMet ? 'SHOW_HIDE' : 'SEQUENTIAL') : 'SEQUENTIAL'
    };
    
  } catch (error) {
    return {
      nextQuestionId: null,
      isTerminal: true,
      hiddenQuestions: new Set(),
      shownQuestions: new Set(),
      appliedRules: [],
      wasJumpTo: false,
      jumpToFromQuestionId: null,
      navigationMethod: 'SEQUENTIAL'
    };
  }
}